import { API_ORIGIN } from '@yeshuman/sdk'

import { workingSessionStorageKey } from './working-session'

export type WorkingSessionGate = {
  masked_email: string
  company: string
  name: string
  status: string
  ended: boolean
  otp_resend_count: number
  otp_max_sends: number
}

export type WorkingSessionLayerCard = {
  layer: string
  fig: string
  claim: string
}

export type WorkingSessionMap = {
  caption: string
  nodes: Array<{ id: string; label: string }>
  edges: Array<{ from: string; to: string }>
} | null

export type WorkingSessionFrame = {
  surface_name: string
  who: string
  primary_action: string
  chrome: string
  work: string
  decision: string
  first_slice_candidate: string
  not_this_session: string
} | null

export type WorkingSessionTable = {
  note: string
  problem: string
  context: string
  requirements: string
  exists: string
  decision: string
  layers: WorkingSessionLayerCard[]
  map: WorkingSessionMap
  frame: WorkingSessionFrame
  phase: string
  status: string
  company: string
  name: string
  ended: boolean
  stance: string
  masked_email?: string
}

function publicUrl(token: string, suffix = ''): string {
  const base = API_ORIGIN ? `${API_ORIGIN}/api/working-sessions/public` : '/api/working-sessions/public'
  return `${base}/${encodeURIComponent(token)}${suffix}`
}

function readAccessToken(token: string): string {
  try {
    return sessionStorage.getItem(workingSessionStorageKey(token)) || ''
  } catch {
    return ''
  }
}

export function writeAccessToken(token: string, accessToken: string): void {
  try {
    sessionStorage.setItem(workingSessionStorageKey(token), accessToken)
  } catch {
    // Ignore private-mode storage failures; OTP can run again.
  }
}

function headersFor(token: string, json = false): HeadersInit {
  const headers: Record<string, string> = {}
  if (json) headers['Content-Type'] = 'application/json'
  const access = readAccessToken(token)
  if (access) headers['X-Working-Session-Key'] = access
  return headers
}

async function readError(response: Response): Promise<string> {
  const raw = await response.text()
  try {
    const data = raw ? (JSON.parse(raw) as { detail?: string; error?: string; message?: string }) : {}
    return data.detail || data.error || data.message || raw || 'Something went wrong'
  } catch {
    return raw || 'Something went wrong'
  }
}

export async function fetchSessionGate(token: string): Promise<WorkingSessionGate> {
  const response = await fetch(publicUrl(token, '/'))
  if (!response.ok) throw new Error(await readError(response))
  return response.json()
}

export async function requestSessionOtp(token: string): Promise<{
  masked_email: string
  cooldown_seconds: number
  sends_remaining: number
}> {
  const response = await fetch(publicUrl(token, '/otp'), { method: 'POST' })
  if (!response.ok) throw new Error(await readError(response))
  return response.json()
}

export async function verifySessionOtp(
  token: string,
  code: string,
): Promise<{ access_token: string; ended: boolean; status: string }> {
  const response = await fetch(publicUrl(token, '/otp/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (!response.ok) throw new Error(await readError(response))
  const data = (await response.json()) as { access_token: string; ended: boolean; status: string }
  writeAccessToken(token, data.access_token)
  return data
}

export async function fetchSessionTable(token: string): Promise<WorkingSessionTable> {
  const response = await fetch(publicUrl(token, '/table'), { headers: headersFor(token) })
  if (!response.ok) throw new Error(await readError(response))
  return response.json()
}

export async function markSessionReady(token: string): Promise<WorkingSessionTable> {
  const response = await fetch(publicUrl(token, '/ready'), {
    method: 'POST',
    headers: headersFor(token),
  })
  if (!response.ok) throw new Error(await readError(response))
  return response.json()
}

export async function endWorkingSession(token: string): Promise<WorkingSessionTable> {
  const response = await fetch(publicUrl(token, '/end'), {
    method: 'POST',
    headers: headersFor(token),
  })
  if (!response.ok) throw new Error(await readError(response))
  return response.json()
}

export async function streamWorkingSessionTurn(
  token: string,
  message: string,
  onEvent: (event: Record<string, unknown>) => void,
): Promise<void> {
  const response = await fetch(publicUrl(token, '/turn'), {
    method: 'POST',
    headers: headersFor(token, true),
    body: JSON.stringify({ message }),
  })
  if (!response.ok) throw new Error(await readError(response))
  if (!response.body) return
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() || ''
    for (const chunk of chunks) {
      const line = chunk
        .split('\n')
        .filter((row) => row.startsWith('data:'))
        .map((row) => row.slice(5).trim())
        .join('')
      if (!line) continue
      try {
        onEvent(JSON.parse(line) as Record<string, unknown>)
      } catch {
        // Ignore malformed SSE frames.
      }
    }
  }
}
